import { z } from 'zod';
import { allowedFilename } from '../../uploadMedia.js';
import { apiClientResp } from '../merchant/error.js';
import { calculatedOrderClaimLineItemReview, calculatedOrderDraft, calculatedOrderShipmentQuote, } from '../schema/calculatedOrder.js';
import { Pegasus } from '../schema/pegasus.js';
import { CrewUi } from './schema.js';
import { orderEditCommitOrStage, orderEditCommitResponse, orderEditPrepareRequest, orderEditPrepareResponse, orderEditStageResponse, orderEditStageResponseUnion, orderEditStartResponse, } from './schema/orderEdit.js';
import { shopNowSession, shopNowSessionCreate, shopNowSessionUpdate, } from './schema/shopNow.js';
import { exchangeRecommendationQuery } from './schema/variantExchange.js';
//* URL params
const settingsParams = z.object({
    slug: z.string(),
});
const storefrontIdParams = z.object({
    storefrontId: z.string(),
});
const storeIdProductParams = z
    .object({
    idFromPlatform: z.string(),
})
    .merge(storefrontIdParams);
const claimIdParams = z
    .object({ claimExternalId: z.coerce.string() })
    .merge(storefrontIdParams);
const gspClaimIdParams = z
    .object({ claimId: z.coerce.number() })
    .merge(storefrontIdParams);
const storeIdFromPlatformQuery = z.object({
    idFromPlatform: z.string(),
    isGiftReturn: z
        .string()
        .transform((val) => val === 'true')
        .optional(),
});
const storeIdOrderNoQuery = z.object({
    orderNo: z.string(),
});
//* URL queries
const storeIdOrderQueryWithEmailOrPostalCode = z
    .object({
    emailOrPostalCode: z.string(),
})
    .merge(storeIdOrderNoQuery);
const storeIdOrderQueryWithGiftReturn = z.object({
    orderOrTrackingNumber: z.string(),
    shippingPostalCode: z.string(),
    isGiftReturn: z.literal('true'),
});
const storeIdOrderUnionQuery = z.union([
    storeIdFromPlatformQuery,
    storeIdOrderQueryWithEmailOrPostalCode,
    storeIdOrderQueryWithGiftReturn,
]);
const signedUrlQuery = z.object({
    filename: allowedFilename,
    destination: z.union([
        z.literal('gspClaimImg'),
        z.literal('crewClaimImg'),
        z.literal('crewProofOfPurchase'),
    ]),
});
//* URL bodies
// ------- URL paths -----------------------------
const shippingOrderPath = `/protection/order`;
const settingsPath = '/settings/:slug';
// ------- everything below here is namespaced by the storefrontId and GSP or CREW where the need to disambiguate exists
const storefrontIdPath = '/:storefrontId';
const registrationPath = `${storefrontIdPath}/registration`;
const registrationProductPath = `${registrationPath}/product`;
const registrationAnalyzePath = `${registrationPath}/analyze`;
const registrationAnalyzeProductPath = `${registrationAnalyzePath}/product`;
const automationsPath = `${storefrontIdPath}/automations`;
const automationsAfterReasonSelectionPath = `${automationsPath}/after-reason-selection`;
const createPaymentIntentPath = `${storefrontIdPath}/create-payment-intent`;
const signedUrlPath = `${storefrontIdPath}/signed-url`;
const storeOrderPath = `${storefrontIdPath}/order`;
const storeOrderEditIdPath = `${storeOrderPath}/:storeOrderId/edit`;
const storeOrderEditIdCommitPath = `${storeOrderEditIdPath}/commit`;
const storeOrderEditIdStagePath = `${storeOrderEditIdPath}/stage`;
const storeOrderEditIdPreparePath = `${storeOrderEditIdPath}/prepare`;
const storeIdProductPath = `${storefrontIdPath}/product/exchange-options/:idFromPlatform`;
const crewClaimPath = `${storefrontIdPath}/claim`;
const crewClaimIdPath = `${crewClaimPath}/:claimExternalId`;
const shippingClaimPath = `${storefrontIdPath}/shipping-protection/claim`;
const shippingClaimIdPath = `${shippingClaimPath}/:shippingClaimId`;
const claimReturnShipmentPath = `${storefrontIdPath}/return-shipment`;
const claimReturnShipmentWhPath = `${claimReturnShipmentPath}/wh`;
// ? quote paths
const claimReturnShipmentQuotePath = `${claimReturnShipmentPath}/quote`;
// order calculate path
const storeIdOrderCalculatePath = `${storeOrderPath}/calculate`;
const storeIdOrderWithdrawPath = `${storeOrderPath}/withdraw`;
const storeOrderCalculatedReasonSelection = `${storeIdOrderCalculatePath}/after-reason-selection`;
const storeOrderCalculateBeforeClaimSubmission = `${storeIdOrderCalculatePath}/before-claim-submission`;
const storeOrderCalculateClaimLineItemReview = `${storeIdOrderCalculatePath}/claim-line-item-review`;
const storefrontShopNowPath = `${storefrontIdPath}/shop-now`;
const storefrontShopNowIdPath = `${storefrontShopNowPath}/:shopNowId`;
const exchangeRequestBody = {
    params: storeIdProductParams,
    query: exchangeRecommendationQuery,
};
const storeOrderParams = storefrontIdParams.extend({
    storeOrderId: z.coerce.number(),
});
const storeOrderShopNowIdParams = storefrontIdParams.extend({
    shopNowId: z.string(),
});
//! all endpoints exposed by the crewUiApi are at a base path of `/v1/shop/crew/`
//! storefrontId is returned in the call to settings, and all subsequent calls require it
/**
 * crewUiApi object containing the following:
 * 1. A list of all routes exposed for consumption by the crew-ui (all routes are prefixed with `/v1/shop/crew/`).
 * 2. All query params, path params, and request/response bodies for each route.
 */
export const crewCustomerApi = {
    // load for GSP flow
    [shippingOrderPath]: {
        path: shippingOrderPath,
        get: {
            request: {
                query: storeIdOrderUnionQuery,
            },
            response: { body: CrewUi.gspOrderSchema },
        },
    },
    // load for CREW flow
    [settingsPath]: {
        path: settingsPath,
        get: {
            request: { params: settingsParams },
            response: { body: CrewUi.settingsSchema },
        },
    },
    [storeIdOrderWithdrawPath]: {
        path: storeIdOrderWithdrawPath,
        post: {
            request: {
                params: storefrontIdParams,
                body: z.object({
                    isDefective: z.boolean(),
                    storeOrderId: z.coerce.number(),
                    address: CrewUi.claimAddressSchema,
                }),
            },
            response: {
                body: z.object({
                    claimExternalId: z.string(),
                }),
            },
        },
    },
    // new calculated order paths
    [storeOrderCalculatedReasonSelection]: {
        path: storeOrderCalculatedReasonSelection,
        post: {
            request: {
                params: storefrontIdParams,
                body: calculatedOrderDraft,
            },
            response: {
                body: CrewUi.calculatedAfterReasonSelectionSchema,
            },
        },
    },
    [storeOrderCalculateClaimLineItemReview]: {
        path: storeOrderCalculateClaimLineItemReview,
        post: {
            request: {
                params: storefrontIdParams,
                body: calculatedOrderClaimLineItemReview,
            },
            response: {
                body: z.object({
                    offerShopNow: Pegasus.shopNowEventParams.optional(),
                }),
            },
        },
    },
    [storeOrderCalculateBeforeClaimSubmission]: {
        path: storeOrderCalculateBeforeClaimSubmission,
        post: {
            request: {
                params: storefrontIdParams,
                body: CrewUi.calculatedOrderSchema,
            },
            response: {
                body: CrewUi.calculatedBeforeClaimSubmissionSchema,
            },
        },
    },
    [registrationPath]: {
        path: registrationPath,
        post: {
            request: {
                params: storefrontIdParams,
                body: CrewUi.registrationCreateSchema,
            },
            response: { body: CrewUi.registrationSchema },
        },
    },
    [registrationAnalyzePath]: {
        path: registrationAnalyzePath,
        post: {
            request: {
                params: storefrontIdParams,
                body: CrewUi.registrationAnalysisRequestSchema,
            },
            response: { body: CrewUi.suggestedRegistrationSchema },
        },
    },
    [registrationAnalyzeProductPath]: {
        path: registrationAnalyzeProductPath,
        post: {
            request: {
                params: storefrontIdParams,
                body: CrewUi.registrationAnalysisProductRequestSchema,
            },
            response: { body: CrewUi.suggestedVariantsResponseSchema },
        },
    },
    [automationsAfterReasonSelectionPath]: {
        path: automationsAfterReasonSelectionPath,
        post: {
            request: {
                params: storefrontIdParams,
                query: z.object({
                    originalStoreOrderLineItemId: z.coerce.number(),
                }),
            },
            response: {
                body: apiClientResp,
            },
        },
    },
    [registrationProductPath]: {
        path: registrationProductPath,
        get: {
            request: {
                params: storefrontIdParams,
                query: z.object({
                    storeSalesChannelName: z.string(),
                    estimatedPurchaseDate: z.string(),
                    searchTerm: z.string(),
                }),
            },
            response: { body: z.array(CrewUi.registrationProductSchema) },
        },
    },
    [shippingClaimPath]: {
        path: shippingClaimPath,
        post: {
            request: {
                params: storefrontIdParams,
                body: CrewUi.shippingClaimCreateCustomerSchema,
            },
            response: { body: CrewUi.shippingClaimSchema },
        },
    },
    [crewClaimPath]: {
        path: crewClaimPath,
        post: {
            request: { params: storefrontIdParams, body: CrewUi.claimCreateSchema },
            response: { body: CrewUi.claimSchema },
        },
    },
    [storeOrderPath]: {
        path: storeOrderPath,
        get: {
            request: {
                params: storefrontIdParams,
                query: storeIdOrderUnionQuery,
            },
            response: { body: CrewUi.orderSchema },
        },
    },
    [crewClaimIdPath]: {
        path: crewClaimIdPath,
        get: {
            request: { params: claimIdParams },
            response: { body: CrewUi.claimSchema },
        },
    },
    [shippingClaimIdPath]: {
        path: shippingClaimIdPath,
        get: {
            request: { params: gspClaimIdParams },
            response: { body: CrewUi.shippingClaimSchema },
        },
    },
    [storeIdProductPath]: {
        path: storeIdProductPath,
        get: {
            request: exchangeRequestBody,
            response: { body: CrewUi.variantExchangeSchema },
        },
    },
    /**
     * @deprecated shipment create is part of the claim create payload
     */
    [claimReturnShipmentPath]: {
        path: claimReturnShipmentPath,
        post: {
            request: {
                params: storefrontIdParams,
                body: CrewUi.shipmentCreateSchema,
            },
            response: { body: apiClientResp },
        },
    },
    [claimReturnShipmentQuotePath]: {
        path: claimReturnShipmentQuotePath,
        post: {
            request: {
                params: storefrontIdParams,
                body: calculatedOrderShipmentQuote,
            },
            response: { body: CrewUi.shipmentQuoteSchema },
        },
    },
    [claimReturnShipmentWhPath]: {
        path: claimReturnShipmentWhPath,
        // WH path for easy post updates
        post: { request: { params: storefrontIdParams } },
    },
    [signedUrlPath]: {
        path: signedUrlPath,
        get: {
            request: { params: storefrontIdParams, query: signedUrlQuery },
            response: { body: CrewUi.signedUrlSchema },
        },
    },
    [createPaymentIntentPath]: {
        path: createPaymentIntentPath,
        post: {
            request: {
                params: storefrontIdParams,
                body: CrewUi.paymentIntentCreateSchema,
            },
            response: { body: CrewUi.paymentIntentSchema },
        },
    },
};
export const crewCustomerOrderEditApi = {
    // ? Get all events that can be applied to an order
    [storeOrderEditIdPath]: {
        path: storeOrderEditIdPath,
        get: {
            request: {
                params: storeOrderParams,
            },
            response: {
                body: orderEditStartResponse,
            },
        },
    },
    // ? optionally prepare to provide stage options
    [storeOrderEditIdPreparePath]: {
        path: storeOrderEditIdPreparePath,
        post: {
            request: {
                params: storeOrderParams,
                body: orderEditPrepareRequest,
            },
            response: {
                body: orderEditPrepareResponse,
            },
        },
    },
    // ? Stage the results
    [storeOrderEditIdStagePath]: {
        path: storeOrderEditIdStagePath,
        post: {
            request: {
                params: storeOrderParams,
                body: orderEditCommitOrStage,
            },
            response: {
                body: orderEditStageResponseUnion,
            },
        },
    },
    // ? Commit the results
    [storeOrderEditIdCommitPath]: {
        path: storeOrderEditIdCommitPath,
        post: {
            request: {
                params: storeOrderParams,
                body: orderEditStageResponse,
            },
            response: {
                body: orderEditCommitResponse,
            },
        },
    },
};
export const crewCustomerShopNowApi = {
    [storefrontShopNowPath]: {
        path: storefrontShopNowPath,
        post: {
            request: {
                params: storefrontIdParams,
                body: shopNowSessionCreate,
            },
            response: {
                body: shopNowSession,
            },
        },
    },
    [storefrontShopNowIdPath]: {
        path: storefrontShopNowIdPath,
        get: {
            request: { params: storeOrderShopNowIdParams },
            response: { body: shopNowSession },
        },
        put: {
            request: {
                params: storeOrderShopNowIdParams,
                body: shopNowSessionUpdate,
            },
            response: {
                body: shopNowSession,
            },
        },
    },
};
//# sourceMappingURL=api.js.map